import { useState, useEffect, useCallback, useRef, RefObject } from "react";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  setStreamingConnected,
  setStreamingError,
  updateDetectionBoxes,
  setStreamingGuidanceMessage,
  setImageNextStage,
  resetStreamingState,
  updateStreamingMetrics,
  initStreamingSession,
} from "src/store/inspectionCaptureSlice";
import ImageStreamingWebSocketService from "src/services/imageStreamingWebSocketService";
import { getWebSocketUrl, generateClientId } from "src/config/websocketConfig";
import { getConfigDrivenQuestionIds } from "../utils/questionIdExtractor";

interface UseStreamingServiceProps {
  lowResVideoRef: RefObject<HTMLVideoElement>;
  highResVideoRef: RefObject<HTMLVideoElement>;
  enabled: boolean;
  onReadyForAnalyzer?: () => void; // Callback when ready for analyzer
  containerId?: string; // Optional drum ID from barcode scan
  targetClasses?: string[]; // Optional target classes for detection
}

interface UseStreamingServiceReturn {
  streamingService: ImageStreamingWebSocketService | null;
  pauseStreaming: () => void;
  resumeStreaming: () => void;
}



/**
 * Custom hook to manage WebSocket streaming service lifecycle
 * Handles connection, reconnection, and cleanup for dual camera system
 */
export const useStreamingService = ({
  lowResVideoRef,
  highResVideoRef,
  enabled,
  onReadyForAnalyzer,
  containerId,
  targetClasses,
}: UseStreamingServiceProps): UseStreamingServiceReturn => {
  const dispatch = useAppDispatch();
  const { 
    streamingConfig, 
    streamingConnected,
    screenWidth,
    screenHeight
  } = useAppSelector(
    (state) => state.inspectionCapture
  );
  const { currentStep, currentIterateStep, inspectionId } = useAppSelector((state) => state.workflow);
  const [streamingService, setStreamingService] = useState<ImageStreamingWebSocketService | null>(null);

  // Handle detection results from WebSocket
  const handleDetectionResult = useCallback((result: any) => {
    if (result.status === "success") {
      // Update detection boxes
      if (result.bboxes) {
        dispatch(updateDetectionBoxes(result.bboxes));
      }
      
      // Update guidance message
      if (result.guidance_message) {
        dispatch(setStreamingGuidanceMessage(result.guidance_message));
      }
      
      // Handle image_next_stage transitions
      if (result.image_next_stage) {
        dispatch(setImageNextStage(result.image_next_stage));
        
        // Switch WebSocket stream based on backend signal
        if (result.image_next_stage === 'HIGH_RES') {
          streamingService?.switchToHighRes();
        } else if (result.image_next_stage === 'LOW_RES') {
          streamingService?.switchToLowRes();
        } else if (result.image_next_stage === 'READY_FOR_ANALYZER') {
          // Stop streaming when ready for analyzer
          streamingService?.pauseStreaming();
          
          // Trigger modal to ask user to move to next
          if (onReadyForAnalyzer) {
            onReadyForAnalyzer();
          }
        }
      }
    }
  }, [dispatch, streamingService]);

  // Handle WebSocket connection status
  const handleConnectionStatus = useCallback((connected: boolean) => {
    dispatch(setStreamingConnected(connected));
    if (!connected) {
      dispatch(setStreamingError("Connection lost"));
    } else {
      dispatch(setStreamingError(null));
    }
  }, [dispatch]);

  // Handle metrics updates from WebSocket
  const handleMetricsUpdate = useCallback((metrics: { imageSize?: number; latency?: number }) => {
    dispatch(updateStreamingMetrics(metrics));
  }, [dispatch]);

  // Initialize WebSocket streaming service
  useEffect(() => {
    if (enabled && !streamingService && lowResVideoRef.current && highResVideoRef.current) {
      const wsUrl = getWebSocketUrl();
      const clientId = generateClientId();

      // Only create service if we have a valid inspection ID
      if (!inspectionId) {
        console.warn("Cannot initialize streaming service: No inspection ID available");
        dispatch(setStreamingError("No inspection ID available for streaming"));
        return;
      }

      // Extract active question IDs
      const activeQuestionIds = getConfigDrivenQuestionIds(
        currentStep,
        currentIterateStep,
      );

      const service = new ImageStreamingWebSocketService(
        wsUrl, 
        clientId, 
        inspectionId,
        activeQuestionIds,
        {
          quality: streamingConfig.quality,
          maxWidth: streamingConfig.maxWidth,
          maxHeight: streamingConfig.maxHeight,
          interval: streamingConfig.interval,
          screenWidth: screenWidth,
          screenHeight: screenHeight,
          resolutionScale: streamingConfig.resolutionScale,
          containerId: containerId,
          targetClasses: targetClasses
        }
      );
      setStreamingService(service);
    }
  }, [enabled, lowResVideoRef.current, highResVideoRef.current, streamingConfig, inspectionId]);

  // Cleanup effect - stops streaming when component unmounts
  // Use a ref to avoid stale closure issues
  const streamingServiceRef = useRef<ImageStreamingWebSocketService | null>(null);

  useEffect(() => {
    streamingServiceRef.current = streamingService;
  }, [streamingService]);

  useEffect(() => {
    return () => {
      const service = streamingServiceRef.current;
      if (service) {
        service.stopStreaming();
        dispatch(resetStreamingState());
      }
    };
  }, [dispatch]);

  // Connection health monitoring
  useEffect(() => {
    if (enabled && !streamingConnected && streamingService) {
      if (streamingService.getConnectionStatus() === 'closed') {
        streamingService.resumeStreaming();

        setTimeout(() => {
          if (!streamingConnected) {
            dispatch(setStreamingError("Reconnection failed"));
          }
        }, 2000);
      }
    }
  }, [enabled, streamingConnected, streamingService]);

  // Start streaming when service is available
  useEffect(() => {
    const startStreaming = async () => {
      if (streamingService && lowResVideoRef.current && highResVideoRef.current && enabled) {
        try {
          await streamingService.startStreaming(
            lowResVideoRef.current,
            highResVideoRef.current,
            handleDetectionResult,
            handleConnectionStatus,
            handleMetricsUpdate
          );

          dispatch(initStreamingSession());
        } catch (error) {
          dispatch(setStreamingError("Failed to connect to streaming service"));
        }
      }
    };

    startStreaming();
  }, [streamingService, lowResVideoRef.current, highResVideoRef.current, enabled]);

  // Pause streaming
  const pauseStreaming = useCallback(() => {
    if (streamingService) {
      streamingService.pauseStreaming();
    }
  }, [streamingService]);

  // Resume streaming
  const resumeStreaming = useCallback(() => {
    if (streamingService && streamingService.isConnected()) {
      streamingService.resumeStreaming();
    }
  }, [streamingService]);

  return {
    streamingService,
    pauseStreaming,
    resumeStreaming,
  };
};
